import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useTresors, useTresor, useBrands } from "@/data/hooks";
import { useLanguage } from "@/i18n/LanguageContext";
import { FadeUp, Stagger, StaggerItem } from "@/components/Reveal";

export default function TresorPage() {
 const { locale } = useLanguage();
 const l = (fr, en, pt) => locale === "en" ? en : locale === "pt" ? pt : fr;
 const { slug } = useParams();
 const t = useTresor(slug);
 const TRESORS = useTresors();
 const BRANDS = useBrands();
 const { scrollY } = useScroll();
 const y = useTransform(scrollY, [0, 600], [0, 100]);

 if (!t) return <Navigate to="/tresors" replace />;

 const linkedBrands = (t.products || []).map((s) => BRANDS.find((b) => b.slug === s)).filter(Boolean);
 const idx = TRESORS.findIndex((x) => x.slug === t.slug);
 const next = TRESORS[(idx + 1) % TRESORS.length];

 return (
 <div data-testid={`page-tresor-${t.slug}`}>
 <section className="relative h-[80vh] min-h-[560px] overflow-hidden bg-nanda-ink">
 <motion.div style={{ y }} className="absolute inset-0">
 {t.image ? (
 <>
 <img src={t.image} alt={t.name} className="h-full w-full object-cover" loading="eager" />
 <div className="absolute inset-0 bg-gradient-to-b from-nanda-ink/30 via-nanda-ink/15 to-nanda-ink/60" />
 </>
 ) : (
 <div className="h-full w-full bg-nanda-ink flex items-center justify-center">
 <span className="font-serif italic text-white/10 text-[20vw] leading-none select-none">{t.latin.split("")[0]}</span>
 </div>
 )}
 </motion.div>
 <div className="relative z-10 mx-auto max-w-[1400px] h-full px-6 md:px-10 lg:px-14 flex flex-col justify-end pb-20 md:pb-24">
 <Link to="/tresors" className="label-overline text-white/80 hover:text-white mb-8 inline-flex items-center gap-2" data-testid="tresor-back">
 <ArrowLeft size={14} strokeWidth={1.5} /> {l("Les Trésors", "The Treasures", "Os Tesouros")}
 </Link>
 <div className="label-overline text-white/85 mb-4">{t.territory}</div>
 <h1 className="font-serif text-white text-6xl md:text-8xl lg:text-9xl leading-[0.95] tracking-tight">{t.name}</h1>
 <div className="italic text-white/75 text-lg mt-5">{t.latin}</div>
 </div>
 </section>

 {/* Story */}
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1100px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp>
 <blockquote className="font-serif text-3xl md:text-5xl leading-[1.15] text-balance">« {t.teaser} »</blockquote>
 </FadeUp>
 <FadeUp delay={0.1} className="mt-16 max-w-2xl text-nanda-inkSoft text-base md:text-[17px] leading-relaxed">
 <p>{t.story}</p>
 </FadeUp>
 </div>
 </section>

 {/* {l("Récolte", "Harvest", "Colheita")} */}
 <section className="border-t border-nanda-line bg-nanda-bgAlt">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
 <FadeUp className="md:col-span-5">
 <div className="label-overline text-nanda-muted mb-4">{l("Récolte", "Harvest", "Colheita")} & {l("territoire", "territory", "território")}</div>
 <h2 className="font-serif text-4xl md:text-5xl leading-[1.05]">{l(`D'où vient ${t.name}.`, `Where ${t.name} comes from.`, `De onde vem ${t.name}.`)}</h2>
 </FadeUp>
 <FadeUp delay={0.1} className="md:col-span-7">
 <p className="text-nanda-inkSoft text-base md:text-[17px] leading-relaxed max-w-2xl">{t.harvest}</p>
 <div className="mt-10">
 <div className="label-overline text-nanda-muted mb-4">{l("Bienfaits", "Benefits", "Benefícios")}</div>
 <ul className="space-y-3">
 {t.benefits.map((b, i) => (
 <li key={i} className="flex items-center gap-3 text-nanda-ink text-[15px]">
 <span className="h-px w-6 bg-nanda-ink" /> {b}
 </li>
 ))}
 </ul>
 </div>
 </FadeUp>
 </div>
 </section>

 {/* Produits NANDA SÁ qui l'utilisent */}
 {linkedBrands.length > 0 && (
 <section className="border-t border-nanda-line">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
 <FadeUp className="mb-14">
 <div className="label-overline text-nanda-muted mb-4">{l("Maisons concernées", "Related Houses", "Casas envolvidas")}</div>
 <h2 className="font-serif text-4xl md:text-5xl">{l("Les produits qui l'utilisent.", "The products that use it.", "Os produtos que o utilizam.")}</h2>
 </FadeUp>
 <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6">
 {linkedBrands.map((b) => (
 <StaggerItem key={b.slug}>
 <Link to={`/marques/${b.slug}`} className="group relative block aspect-[16/9] overflow-hidden border border-nanda-line" data-testid={`tresor-brand-${b.slug}`}>
 <img src={b.image} alt={b.name} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
 <div className="absolute inset-0 bg-gradient-to-t from-nanda-ink/75 via-nanda-ink/15 to-transparent" />
 <div className="absolute inset-0 p-8 flex flex-col justify-end">
 <div className="font-serif text-white text-3xl md:text-4xl">{b.name}</div>
 <div className="label-overline text-white/80 mt-2">{b.category}</div>
 </div>
 </Link>
 </StaggerItem>
 ))}
 </Stagger>
 </div>
 </section>
 )}

 {/* Next */}
 <section className="border-t border-nanda-line bg-nanda-ink text-nanda-bg">
 <Link to={`/tresors/${next.slug}`} className="block hover:bg-nanda-inkSoft transition-colors duration-500" data-testid="tresor-next">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-20 md:py-28 flex flex-col md:flex-row md:items-center justify-between gap-8">
 <div>
 <div className="label-overline text-nanda-bg/60 mb-3">{l("Trésor suivant", "Next treasure", "Próximo tesouro")}</div>
 <div className="font-serif text-4xl md:text-6xl text-nanda-bg">{next.name}</div>
 </div>
 <ArrowUpRight size={36} strokeWidth={1.2} className="text-nanda-bg" />
 </div>
 </Link>
 </section>
 </div>
 );
}
